import { useMemo, useState } from 'react';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  refetchEinvoicingAdminDataQuery,
  useAcceptPeppolRegistrationMutation,
  useAssignPeppolInboxMutation,
  useDenyPeppolRegistrationMutation,
  useEinvoicingAdminDataQuery,
  useSyncFakturoidCompanyDetailsMutation,
  useUnassignPeppolInboxMutation,
  useUnassignPeppolVenueMutation,
} from '../../generated/graphql';
import {
  type AdminVenue,
  type InboxOption,
  type PendingRequest,
  buildInboxOptions,
  buildPendingRequests,
  idsDiffer,
} from './adminEinvoicingUtils';

type EntryField = 'venue' | 'inbox';

export const EinvoicingIntegrationPage = () => {
  const { data, loading, refetch } = useEinvoicingAdminDataQuery({
    fetchPolicy: 'cache-and-network',
  });
  const venues = data?.einvoicingAdminVenues ?? [];
  const inboxes = data?.einvoicingAdminInboxes ?? [];
  const queue = data?.peppolRegistrationQueue ?? [];
  const requests = useMemo(
    () => buildPendingRequests(venues, queue),
    [venues, queue],
  );

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [peppolDraft, setPeppolDraft] = useState('');
  const [entry, setEntry] = useState<EntryField | null>(null);
  const [activeRequestId, setActiveRequestId] = useState<string | null>(null);
  const [denyId, setDenyId] = useState<string | null>(null);
  const [confirmAction, setConfirmAction] = useState<
    'assign' | 'unassign' | null
  >(null);
  const [syncResult, setSyncResult] = useState<{
    synced: number;
    failed: number;
  } | null>(null);

  const [syncFakturoidCompanyDetails, { loading: syncing }] =
    useSyncFakturoidCompanyDetailsMutation();
  const [assignPeppolInbox] = useAssignPeppolInboxMutation();
  const [unassignPeppolVenue] = useUnassignPeppolVenueMutation();
  const [unassignPeppolInbox] = useUnassignPeppolInboxMutation();
  const [denyPeppolRegistration] = useDenyPeppolRegistrationMutation();
  const [acceptPeppolRegistration] = useAcceptPeppolRegistrationMutation();

  const refetchAdmin = () => refetch();

  const handleSyncFakturoidDetails = async () => {
    setSyncResult(null);
    const result = await syncFakturoidCompanyDetails({
      variables: {},
      toastConfig: {
        successText: 'Fakturoid company details sync finished',
        errorText: 'Failed to sync Fakturoid company details',
      },
    });
    const counts = result?.data?.syncFakturoidCompanyDetails;
    if (counts) {
      setSyncResult({ synced: counts.synced, failed: counts.failed });
      await refetchAdmin();
    }
  };

  const selected = venues.find((venue) => venue.id === selectedId) ?? null;
  const activeRequest =
    requests.find((request) => request.id === activeRequestId) ?? null;

  const inboxOptions = useMemo(
    () => buildInboxOptions(venues, inboxes, requests),
    [venues, inboxes, requests],
  );

  const options = useMemo(
    () => [...venues].sort((a, b) => a.name.localeCompare(b.name)),
    [venues],
  );

  const resetSearch = () => {
    setSelectedId(null);
    setPeppolDraft('');
    setEntry(null);
    setActiveRequestId(null);
  };

  const runAssign = async () => {
    if (!selected || !peppolDraft) return;
    const result = await assignPeppolInbox({
      variables: {
        input: {
          venueId: selected.id,
          peppolParticipantId: peppolDraft,
        },
      },
      toastConfig: { successText: 'Inbox assigned' },
      refetchQueries: [refetchEinvoicingAdminDataQuery()],
    });
    if (result) resetSearch();
  };

  const runUnassign = async () => {
    let result: unknown = null;
    if (entry === 'inbox' && peppolDraft) {
      result = await unassignPeppolInbox({
        variables: { peppolParticipantId: peppolDraft },
        toastConfig: { successText: 'Inbox unassigned' },
        refetchQueries: [refetchEinvoicingAdminDataQuery()],
      });
    } else if (selected) {
      result = await unassignPeppolVenue({
        variables: { venueId: selected.id },
        toastConfig: { successText: 'Inbox unassigned' },
        refetchQueries: [refetchEinvoicingAdminDataQuery()],
      });
    }
    if (result) resetSearch();
  };

  const runDeny = async () => {
    if (!denyId) return;
    await denyPeppolRegistration({
      variables: { registrationId: denyId },
      toastConfig: null,
      refetchQueries: [refetchEinvoicingAdminDataQuery()],
    });
    setDenyId(null);
    if (activeRequestId === denyId) setActiveRequestId(null);
  };

  const activePeppolId = peppolDraft || selected?.peppolParticipantId || '';
  const selectedInbox = useMemo(
    () =>
      inboxes.find((inbox) => inbox.peppolParticipantId === activePeppolId) ??
      null,
    [inboxes, activePeppolId],
  );

  const linkedVenues = venues.filter(
    (venue) => peppolDraft && venue.peppolParticipantId === peppolDraft,
  );
  const inboxLocked =
    entry === 'venue' && Boolean(selected?.peppolParticipantId);
  const venueLocked = entry === 'inbox' && linkedVenues.length > 1;
  const alreadyLinked = Boolean(
    selected && peppolDraft && selected.peppolParticipantId === peppolDraft,
  );

  const openRequest = (request: PendingRequest) => {
    const assigned = venues.filter(
      (venue) => venue.peppolParticipantId === request.peppolId,
    );
    const suggested = venues.find(
      (venue) => venue.id === request.suggestedVenueId,
    );
    setEntry('inbox');
    setActiveRequestId(request.id);
    setPeppolDraft(request.peppolId);
    if (assigned.length === 1) {
      setSelectedId(assigned[0].id);
      return;
    }
    setSelectedId(
      assigned.length === 0 && suggested && !suggested.peppolParticipantId
        ? suggested.id
        : null,
    );
  };

  const acceptRequest = async (request: PendingRequest) => {
    if (request.kind === 'none') return;
    await acceptPeppolRegistration({
      variables: { registrationId: request.id },
      toastConfig: null,
      refetchQueries: [refetchEinvoicingAdminDataQuery()],
    });
    if (activeRequestId === request.id) setActiveRequestId(null);
  };

  return (
    <Box width="100%" p={3} display="flex" justifyContent="center">
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '1fr',
          gap: 2,
          maxWidth: '1400px',
          width: '100%',
        }}
      >
        <RequestQueue
          requests={requests}
          venues={venues}
          onReview={openRequest}
          onAccept={acceptRequest}
          onDeny={setDenyId}
        />
        <Card sx={{ p: 3, backgroundColor: '#FAFBFF' }}>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            mb={1}
          >
            <Typography variant="h5" gutterBottom sx={{ mb: 0 }}>
              E-invoicing (SK)
            </Typography>
            <Box display="flex" alignItems="center" gap={1}>
              {syncResult ? (
                <Typography variant="body2" color="text.secondary">
                  Synced {syncResult.synced}, failed {syncResult.failed}
                </Typography>
              ) : null}
              <Button
                size="small"
                variant="outlined"
                disabled={syncing || loading}
                onClick={handleSyncFakturoidDetails}
                startIcon={syncing ? <CircularProgress size={16} /> : undefined}
              >
                Sync Fakturoid details
              </Button>
            </Box>
          </Box>
          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
            Venue
          </Typography>
          {venueLocked && linkedVenues.length > 1 ? (
            <TextField
              fullWidth
              disabled
              label="Search venue"
              value={linkedVenues.map((venue) => venue.name).join(', ')}
            />
          ) : (
            <Autocomplete
              options={options}
              value={venueLocked ? linkedVenues[0] ?? selected : selected}
              disabled={venueLocked}
              getOptionLabel={(venue) => venue.name}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              getOptionDisabled={(venue) =>
                entry === 'inbox' &&
                Boolean(peppolDraft) &&
                venue.peppolParticipantId != null &&
                venue.peppolParticipantId !== peppolDraft
              }
              onChange={(_, venue) => {
                if (entry === 'inbox' && peppolDraft) {
                  setSelectedId(venue?.id ?? null);
                  return;
                }
                if (!venue) {
                  resetSearch();
                  return;
                }
                setEntry('venue');
                setSelectedId(venue.id);
                setPeppolDraft(venue.peppolParticipantId ?? '');
                setActiveRequestId(
                  requests.find(
                    (request) => request.peppolId === venue.peppolParticipantId,
                  )?.id ?? null,
                );
              }}
              renderOption={(props, venue) => (
                <Box component="li" {...props}>
                  <Box>
                    <Typography variant="body2">{venue.name}</Typography>
                    <Box display="flex" alignItems="center">
                      <StatusMark
                        connected={venue.peppolParticipantId != null}
                      />
                      <Typography variant="body2" color="text.secondary">
                        {venue.peppolParticipantId
                          ? `Connected · ${venue.peppolParticipantId}`
                          : 'Not connected'}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              )}
              renderInput={(params) => (
                <TextField {...params} label="Search venue" />
              )}
            />
          )}
          <Typography
            variant="subtitle1"
            sx={{ mt: 2, mb: 1, fontWeight: 600 }}
          >
            Digital Inbox
          </Typography>
          <InboxSearch
            inboxes={inboxOptions}
            value={peppolDraft}
            disabled={inboxLocked}
            isOptionDisabled={(item) =>
              Boolean(
                selected &&
                  item.assignedVenues.includes(selected.name) &&
                  selected.peppolParticipantId === item.peppolId,
              )
            }
            onChange={(peppolId) => {
              if (
                entry === 'venue' &&
                selected &&
                !selected.peppolParticipantId
              ) {
                setPeppolDraft(peppolId);
                return;
              }
              if (!peppolId) {
                resetSearch();
                return;
              }
              const request = requests.find(
                (item) => item.peppolId === peppolId,
              );
              const assigned = venues.filter(
                (venue) => venue.peppolParticipantId === peppolId,
              );
              const suggested = venues.find(
                (venue) => venue.id === request?.suggestedVenueId,
              );
              setEntry('inbox');
              setPeppolDraft(peppolId);
              setActiveRequestId(request?.id ?? null);
              if (assigned.length === 1) {
                setSelectedId(assigned[0].id);
                return;
              }
              setSelectedId(
                assigned.length === 0 &&
                  suggested &&
                  !suggested.peppolParticipantId
                  ? suggested.id
                  : null,
              );
            }}
          />
          {selected || peppolDraft ? (
            <VenueDetail
              venue={selected}
              peppolDraft={peppolDraft || selected?.peppolParticipantId || ''}
              legalName={selectedInbox?.legalName ?? selected?.name ?? '—'}
              ico={selectedInbox?.ico ?? selected?.ico ?? '—'}
              dic={selectedInbox?.dic ?? selected?.dic ?? '—'}
              pending={
                selected && activeRequest?.suggestedVenueId === selected.id
                  ? activeRequest
                  : null
              }
              incoming={
                activeRequest &&
                !(selected && activeRequest.suggestedVenueId === selected.id)
                  ? activeRequest
                  : null
              }
              onAssign={() => setConfirmAction('assign')}
              alreadyLinked={alreadyLinked}
              onUnassign={() => setConfirmAction('unassign')}
              onConfirm={() => {
                if (!activeRequest || !selected) return;
                setConfirmAction('assign');
              }}
              onDeny={() => {
                if (activeRequest) setDenyId(activeRequest.id);
              }}
            />
          ) : null}
        </Card>
      </Box>
      <Dialog
        open={confirmAction != null}
        onClose={() => setConfirmAction(null)}
      >
        <DialogTitle>
          {confirmAction === 'unassign'
            ? 'Unassign this inbox?'
            : 'Assign this inbox?'}
        </DialogTitle>
        <DialogContent>
          {confirmAction === 'unassign' ? (
            <Alert severity="error">
              {unassignSentence(linkedVenues, selected, entry, peppolDraft)}{' '}
              E-invoices that arrive after this will not be added to Documents.
              Invoices already created stay. This does not release the company
              at the access point.
            </Alert>
          ) : (
            <Alert severity="warning">
              {selected?.name} will be connected to{' '}
              {peppolDraft || selected?.peppolParticipantId}. Every e-invoice
              that arrives in this inbox becomes a document of this venue.
              Assign it only if this inbox belongs to this company.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmAction(null)}>Cancel</Button>
          <Button
            variant="contained"
            color={confirmAction === 'unassign' ? 'error' : 'warning'}
            onClick={async () => {
              if (confirmAction === 'unassign') {
                await runUnassign();
              } else if (selected) {
                await runAssign();
              }
              setConfirmAction(null);
            }}
          >
            {confirmAction === 'unassign' ? 'Unassign inbox' : 'Assign inbox'}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={denyId != null} onClose={() => setDenyId(null)}>
        <DialogTitle>Deny this registration?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Nothing is sent to the access point, and the firm is emailed to
            choose another provider. This is not a release. Use it only when
            this DIČ is clearly not a Dishboard company.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDenyId(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={runDeny}>
            Deny registration
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const MATCH_LABEL = {
  match: 'Match',
  partial: 'Partial',
  none: 'No match',
} as const;

const matchColor = (kind: PendingRequest['kind']) => {
  if (kind === 'match') return 'success' as const;
  if (kind === 'partial') return 'warning' as const;
  return 'default' as const;
};

const matchedVenues = (venues: AdminVenue[], request: PendingRequest) => {
  if (request.kind === 'match') {
    return venues.filter(
      (venue) => venue.ico === request.ico && venue.dic === request.dic,
    );
  }
  if (request.kind === 'partial' && request.suggestedVenueId) {
    const suggested = venues.find(
      (venue) => venue.id === request.suggestedVenueId,
    );
    return suggested ? [suggested] : [];
  }
  return [];
};

const RequestQueue = ({
  requests,
  venues,
  onReview,
  onAccept,
  onDeny,
}: {
  requests: PendingRequest[];
  venues: AdminVenue[];
  onReview: (request: PendingRequest) => void;
  onAccept: (request: PendingRequest) => void;
  onDeny: (requestId: string) => void;
}) => (
  <Card sx={{ p: 3 }}>
    <Typography variant="h6" gutterBottom>
      Pending requests
    </Typography>
    {requests.length === 0 ? (
      <Typography variant="body2" color="text.secondary">
        No registrations are waiting for review.
      </Typography>
    ) : (
      <Table size="small">
        <TableHead>
          <TableRow>
            {[
              'Company',
              'IČO',
              'DIČ',
              'Peppol ID',
              'Match',
              'Matched venues',
              'Received at',
              'Actions',
            ].map((label) => (
              <TableCell
                key={label}
                sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}
              >
                {label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {requests.map((request) => (
            <TableRow key={request.id} hover>
              <TableCell>
                <Button
                  size="small"
                  onClick={() => onReview(request)}
                  sx={{ textTransform: 'none', px: 0 }}
                >
                  {request.legalName}
                </Button>
              </TableCell>
              <TableCell>{request.ico}</TableCell>
              <TableCell sx={{ whiteSpace: 'nowrap' }}>{request.dic}</TableCell>
              <TableCell sx={{ fontFamily: 'monospace', whiteSpace: 'nowrap' }}>
                {request.peppolId}
              </TableCell>
              <TableCell>
                <Chip
                  size="small"
                  label={MATCH_LABEL[request.kind]}
                  color={matchColor(request.kind)}
                />
              </TableCell>
              <TableCell>
                {matchedVenues(venues, request)
                  .map((venue) => venue.name)
                  .join(', ') || '—'}
              </TableCell>
              <TableCell sx={{ whiteSpace: 'nowrap' }}>
                {new Date(request.receivedAt).toLocaleString('en-GB', {
                  dateStyle: 'medium',
                  timeStyle: 'short',
                })}
              </TableCell>
              <TableCell sx={{ whiteSpace: 'nowrap' }}>
                <Button
                  size="small"
                  disabled={request.kind === 'none'}
                  onClick={() => onAccept(request)}
                  sx={{ textTransform: 'none' }}
                >
                  Accept
                </Button>
                <Button
                  size="small"
                  color="error"
                  onClick={() => onDeny(request.id)}
                  sx={{ textTransform: 'none' }}
                >
                  Deny
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    )}
  </Card>
);

const unassignSentence = (
  linked: AdminVenue[],
  selected: AdminVenue | null,
  entry: EntryField | null,
  peppolId: string,
) => {
  const names = entry === 'inbox' ? linked.map((venue) => venue.name) : [];
  if (entry !== 'inbox' && selected) names.push(selected.name);
  const listed = names.join(', ');
  const id = peppolId || selected?.peppolParticipantId || '';
  if (names.length > 1) {
    return `${listed} will all be disconnected from ${id}. These venues will show as not connected.`;
  }
  return `${listed} will be disconnected from ${id}. The venue will show as not connected.`;
};

const VenueDetail = ({
  venue,
  peppolDraft,
  legalName,
  ico,
  dic,
  pending,
  incoming,
  alreadyLinked,
  onAssign,
  onUnassign,
  onConfirm,
  onDeny,
}: {
  venue: AdminVenue | null;
  peppolDraft: string;
  legalName: string;
  ico: string;
  dic: string;
  pending: PendingRequest | null;
  incoming: PendingRequest | null;
  alreadyLinked: boolean;
  onAssign: () => void;
  onUnassign: () => void;
  onConfirm: () => void;
  onDeny: () => void;
}) => (
  <Box mt={2} p={2} bgcolor="#F3F6FF" borderRadius={1}>
    {venue ? (
      <>
        <Typography variant="body2">
          <strong>Legal name:</strong> {legalName}
        </Typography>
        <Typography variant="body2">
          <strong>IČO:</strong> {ico}
        </Typography>
        <Typography variant="body2">
          <strong>DIČ:</strong> {dic}
        </Typography>
        <Typography variant="body2">
          <strong>Peppol ID:</strong>{' '}
          {(venue.peppolParticipantId ?? peppolDraft) || 'Not assigned'}
        </Typography>
      </>
    ) : (
      <Typography variant="body2">
        {alreadyLinked
          ? 'This inbox is connected to more than one venue.'
          : 'No venue is selected. Choose a venue that is not connected yet.'}
      </Typography>
    )}
    {incoming ? (
      <Alert severity="info" sx={{ mt: 2 }}>
        {incoming.legalName} · IČO {incoming.ico} · DIČ {incoming.dic} ·{' '}
        {incoming.peppolId}. {incoming.notes.join(' ')}
      </Alert>
    ) : null}
    {venue && incoming && idsDiffer(venue, incoming) ? (
      <Alert severity="error" sx={{ mt: 2 }}>
        This venue&apos;s IČO and DIČ do not match this registration. Assigning
        the inbox here is most likely incorrect.
      </Alert>
    ) : null}
    {pending ? (
      <Alert severity="warning" sx={{ mt: 2 }}>
        Suggested assignment for {pending.legalName}. {pending.notes.join(' ')}
        <Box display="flex" gap={1} mt={1}>
          <Button size="small" variant="contained" onClick={onConfirm}>
            Confirm suggestion
          </Button>
          <Button size="small" color="error" onClick={onDeny}>
            Deny
          </Button>
        </Box>
      </Alert>
    ) : null}
    <Box display="flex" gap={1} mt={2}>
      {alreadyLinked ? null : (
        <Button
          variant="contained"
          disabled={!venue || !peppolDraft.trim().startsWith('0245:')}
          onClick={onAssign}
          sx={{ height: 'auto', py: 1, lineHeight: 1.2 }}
        >
          Assign inbox
        </Button>
      )}
      {alreadyLinked ? (
        <Button color="error" onClick={onUnassign}>
          Unassign inbox
        </Button>
      ) : null}
    </Box>
  </Box>
);

const StatusMark = ({ connected }: { connected: boolean }) =>
  connected ? (
    <CheckCircleOutlineIcon
      sx={{ fontSize: 15, color: 'success.main', opacity: 0.65, mr: 0.5 }}
    />
  ) : (
    <CancelOutlinedIcon
      sx={{ fontSize: 15, color: 'error.main', opacity: 0.65, mr: 0.5 }}
    />
  );

const InboxSearch = ({
  inboxes,
  value,
  disabled,
  isOptionDisabled,
  onChange,
}: {
  inboxes: InboxOption[];
  value: string;
  disabled: boolean;
  isOptionDisabled: (item: InboxOption) => boolean;
  onChange: (peppolId: string) => void;
}) => (
  <Autocomplete
    options={inboxes}
    disabled={disabled}
    value={inboxes.find((item) => item.peppolId === value) ?? null}
    getOptionLabel={(item) => item.peppolId}
    isOptionEqualToValue={(option, item) => option.peppolId === item.peppolId}
    getOptionDisabled={isOptionDisabled}
    filterOptions={(items, state) => {
      const query = state.inputValue.trim().toLowerCase();
      if (!query) return items;
      return items.filter((item) =>
        [
          item.peppolId,
          item.legalName,
          item.ico,
          item.dic,
          item.pendingNote ?? '',
          ...item.assignedVenues,
        ]
          .join(' ')
          .toLowerCase()
          .includes(query),
      );
    }}
    onChange={(_, item) => onChange(item?.peppolId ?? '')}
    renderOption={(props, item) => (
      <Box component="li" {...props}>
        <Box py={0.5}>
          <Typography variant="body2" fontFamily="monospace">
            {item.peppolId}
          </Typography>
          <Typography variant="body2">{item.legalName}</Typography>
          <Typography variant="body2" color="text.secondary">
            IČO {item.ico} · DIČ {item.dic}
          </Typography>
          <Box display="flex" alignItems="center">
            <StatusMark connected={item.assignedVenues.length > 0} />
            <Typography variant="body2" color="text.secondary">
              {item.assignedVenues.length > 0
                ? `Connected · ${item.assignedVenues.join(', ')}`
                : 'Not connected'}
            </Typography>
          </Box>
          {item.pendingNote ? (
            <Typography variant="body2" color="text.secondary">
              Pending registration: {item.pendingNote}
            </Typography>
          ) : null}
        </Box>
      </Box>
    )}
    renderInput={(params) => (
      <TextField {...params} label="Search digital inbox" />
    )}
  />
);
